import { useCallback, useMemo, useRef } from "react";

import {
  ConversationStatus,
  getConversationStatus,
} from "../components/conversationStatus";

// Turn starters keep their own signatures; this hook only forwards them.
type AnyAction = (...args: any[]) => any;

/**
 * A newer Assistant message than the baseline — not merely a different last
 * id. A `lastIndexOf` of -1 means the baseline is not (yet) in the live chain,
 * which is a rebuild still streaming rather than new content.
 */
const grewAfter = (liveAssistantIds: string[], baselineId: string | null) => {
  if (baselineId === null) return liveAssistantIds.length > 0;
  const at = liveAssistantIds.lastIndexOf(baselineId);
  return at >= 0 && at < liveAssistantIds.length - 1;
};

/**
 * The AtomAssist page's conversation-status wiring.
 *
 * "complete" on the tab disc and the header chip means *a response you have
 * not seen yet*, so it may only be reported for a turn that actually ran in
 * this tab. Two things make that harder than watching the streaming flags:
 *
 * - Switching chats does not remount the page: `TheContent` keys routes by
 *   index, `AtomAssistChatPage` renders the page without a key, and
 *   `isChatReady()` is just `!!state.config`. So "a turn ran" has to be scoped
 *   to a dialogue, not to the mount.
 * - Reopening a chat inside the backend's idle-session window (300s, or 1800s
 *   while a prompt waits on the user) reconnects: `connectToStream` sets
 *   `isLoading`/`isTurnActive` immediately and the reconcile loop then
 *   re-emits `end`. The streaming flags alone cannot tell that from a turn.
 *
 * A turn therefore only counts when one of two things happened for *this*
 * dialogue:
 *  1. the user started it through one of the actions wrapped here — the
 *     composer's send/resend/regenerate and the splash's empty submission.
 *     `queueMessage`/`processMessageQueue` and prompt replies through
 *     `delivery.ts` bypass the wrapper and rely on signal 2; that fails
 *     closed, costing a disc rather than inventing one.
 *  2. the live dialogue grew an Assistant message *after* the one it was
 *     already holding for this chat. Only a turn that ran while this tab was
 *     attached can do that: reconnecting to an *idle* session resumes with no
 *     new content, and the legacy replay and `retryConnection` rebuilds
 *     restore ids the baseline already covers. "After", not "differs from":
 *     a rebuild streams those ids back over several animation frames, so
 *     mid-rebuild the last id is an older one.
 *
 * Signal 2 is derived during render, so the first render after a chat switch
 * is already right — an effect-based reset would report "complete" for one
 * render — while signal 1 is set from the wrapped action's callback. Both are
 * scoped to `dialogueId` and both are dropped when it changes. Signal 2 is
 * also ignored until the live store has been adopted for this dialogue:
 * `useOwnChatStream` adopts it *during* render, so the pass that begins a
 * switch is still bound to the previous chat's store.
 */
export const useConversationStatusInputs = <
  Send extends AnyAction,
  Resend extends AnyAction,
  Regenerate extends AnyAction,
  Empty extends AnyAction,
>({
  dialogueId,
  hasConversation,
  isComplete,
  isError,
  isOnboarding,
  isWorking,
  historyLoaded,
  historyLastAssistantId = null,
  liveDialogueId = null,
  liveAssistantIds = [],
  sendUserMessage,
  resendUserMessage,
  regenerateResponse,
  emptySubmission,
}: {
  /** The dialogue on screen. A switch re-scopes everything below. */
  dialogueId?: number | null;
  hasConversation: boolean;
  isComplete: boolean;
  isError: boolean;
  isOnboarding: boolean;
  isWorking: boolean;
  /** True once the REST snapshot for *this* dialogueId has loaded. */
  historyLoaded: boolean;
  /** Newest Assistant message id in that snapshot; null when it has none. */
  historyLastAssistantId?: string | null;
  /** The dialogue the live streaming store is bound to (`streamConfig`). */
  liveDialogueId?: number | null;
  /** Assistant message ids in the live (streaming) dialogue, in order. */
  liveAssistantIds?: string[];
  sendUserMessage: Send;
  resendUserMessage: Resend;
  regenerateResponse: Regenerate;
  emptySubmission: Empty;
}): {
  conversationStatus: ConversationStatus;
  /** The same actions, each marking a user-started turn for this dialogue. */
  turnActions: {
    sendUserMessage: Send;
    resendUserMessage: Resend;
    regenerateResponse: Regenerate;
    emptySubmission: Empty;
  };
} => {
  // The dialogue a turn has been seen for. `undefined` means none yet, which a
  // null dialogueId must not match.
  const turnSeenFor = useRef<number | null | undefined>(undefined);
  // Where this dialogue's Assistant chain stood when the page first saw it.
  const baseline = useRef<{
    dialogueId: number | null;
    lastAssistantId: string | null;
  } | null>(null);

  const dialogueKey = dialogueId ?? null;
  // Both signals are per dialogue, so a switch drops both. Keeping
  // `turnSeenFor` would report "complete" again for a turn the user had
  // already read the moment they came back to that chat.
  const dialogueKeyRef = useRef<number | null | undefined>(undefined);
  if (dialogueKeyRef.current !== dialogueKey) {
    dialogueKeyRef.current = dialogueKey;
    baseline.current = null;
    turnSeenFor.current = undefined;
  }

  const liveIsThisDialogue = (liveDialogueId ?? null) === dialogueKey;
  const liveLastAssistantId = liveIsThisDialogue
    ? (liveAssistantIds[liveAssistantIds.length - 1] ?? null)
    : null;

  if (!baseline.current && historyLoaded) {
    baseline.current = {
      dialogueId: dialogueKey,
      // The live chain wins when it is this dialogue's: `["dialogue", id]` is
      // never invalidated after a turn, so revisiting a chat serves a snapshot
      // from before it, which would read as new content.
      lastAssistantId: liveLastAssistantId ?? historyLastAssistantId,
    };
  }
  if (
    baseline.current &&
    liveIsThisDialogue &&
    grewAfter(liveAssistantIds, baseline.current.lastAssistantId)
  ) {
    turnSeenFor.current = dialogueKey;
  }

  const markUserTurn = useCallback(() => {
    if (dialogueKeyRef.current === null) return;
    turnSeenFor.current = dialogueKeyRef.current;
  }, []);

  const wrap = useCallback(
    <F extends AnyAction>(action: F) =>
      ((...args: Parameters<F>) => {
        markUserTurn();
        return action(...args);
      }) as F,
    [markUserTurn],
  );

  const turnActions = useMemo(
    () => ({
      sendUserMessage: wrap(sendUserMessage),
      resendUserMessage: wrap(resendUserMessage),
      regenerateResponse: wrap(regenerateResponse),
      emptySubmission: wrap(emptySubmission),
    }),
    [
      wrap,
      sendUserMessage,
      resendUserMessage,
      regenerateResponse,
      emptySubmission,
    ],
  );

  const conversationStatus = getConversationStatus({
    hasConversation,
    isComplete,
    isError,
    isOnboarding,
    isWorking,
    turnSeenForDialogue:
      turnSeenFor.current !== undefined && turnSeenFor.current === dialogueKey,
  });

  return { conversationStatus, turnActions };
};

export default useConversationStatusInputs;
